import React from 'react';
import { Plane, Mail, MapPin, Facebook, Twitter, Instagram, Youtube, ShieldCheck, BadgeCheck, Lock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export default function Footer({ onNavigate }) {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: t('header.home'), target: 'home' },
    { label: t('header.flights'), target: 'flights' },
    { label: t('header.hotels'), target: 'hotels' },
    { label: t('header.packages'), target: 'packages' },
    { label: t('footer.partners'), target: 'partners' },
  ];

  const supportLinks = [
    { label: t('footer.helpCenter'), target: 'help' },
    { label: t('footer.contactUs'), target: 'contact' },
    { label: t('footer.faq'), target: 'faq' },
    { label: t('footer.privacy'), target: 'privacy' },
    { label: t('footer.terms'), target: 'terms' },
  ];

  return (
    <footer className="bg-midnight-900 text-ivory-200" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 border border-gold-400/60 rounded-none flex items-center justify-center">
                <Plane className="w-5 h-5 text-gold-400" />
              </div>
              <span className="font-serif italic text-2xl text-ivory">TravelAI</span>
            </div>
            <p className="text-sm text-ivory-200/70 leading-relaxed font-serif italic">
              {t('footer.brandTagline')}
            </p>
            <div className="flex space-x-3 pt-2">
              <a href="#" aria-label="Facebook" className="w-9 h-9 border border-gold-400/30 flex items-center justify-center hover:border-gold-400 text-ivory-200/70 hover:text-gold-300 transition-colors">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="#" aria-label="Twitter" className="w-9 h-9 border border-gold-400/30 flex items-center justify-center hover:border-gold-400 text-ivory-200/70 hover:text-gold-300 transition-colors">
                <Twitter className="w-4 h-4" />
              </a>
              <a href="#" aria-label="Instagram" className="w-9 h-9 border border-gold-400/30 flex items-center justify-center hover:border-gold-400 text-ivory-200/70 hover:text-gold-300 transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="#" aria-label="YouTube" className="w-9 h-9 border border-gold-400/30 flex items-center justify-center hover:border-gold-400 text-ivory-200/70 hover:text-gold-300 transition-colors">
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-gold-300 text-[10px] uppercase tracking-[0.32em] font-semibold mb-5">{t('footer.quickLinks')}</h3>
            <ul className="space-y-3">
              {quickLinks.map((item) => (
                <li key={item.target}>
                  <button
                    data-testid={`footer-link-${item.target}`}
                    onClick={() => onNavigate && onNavigate(item.target)}
                    className="text-ivory-200/70 hover:text-gold-300 transition-colors text-sm font-serif"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-gold-300 text-[10px] uppercase tracking-[0.32em] font-semibold mb-5">{t('footer.support')}</h3>
            <ul className="space-y-3">
              {supportLinks.map((item) => (
                <li key={item.target}>
                  <button
                    type="button"
                    data-testid={`footer-support-${item.target}`}
                    onClick={() => onNavigate && onNavigate(item.target)}
                    className="text-ivory-200/70 hover:text-gold-300 transition-colors text-sm text-left font-serif"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-gold-300 text-[10px] uppercase tracking-[0.32em] font-semibold mb-5">{t('footer.contact')}</h3>
            <ul className="space-y-3">
              <li className="flex items-center space-x-3 text-sm">
                <MapPin className="w-4 h-4 text-gold-400" />
                <span className="text-ivory-200/70 font-serif">{t('footer.location')}</span>
              </li>
              <li className="flex items-center space-x-3 text-sm">
                <Mail className="w-4 h-4 text-gold-400" />
                <a
                  href="mailto:concierge@travelaibg.com"
                  data-testid="footer-email"
                  className="text-ivory-200/70 hover:text-gold-300 transition-colors break-all font-serif"
                >
                  concierge@travelaibg.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Practical supplier handoff information — no unverified claims
            of IATA accreditation, GDPR certification or payment guarantees. */}
        <div
          className="mt-12 pt-8 border-t border-gold-400/20 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm"
          data-testid="footer-trust-strip"
        >
          <div className="flex items-center gap-2.5 text-ivory-200/70">
            <BadgeCheck className="w-4 h-4 text-gold-400 flex-shrink-0" />
            <span className="font-serif">{t('footer.trust.iata')}</span>
          </div>
          <div className="flex items-center gap-2.5 text-ivory-200/70">
            <Lock className="w-4 h-4 text-gold-400 flex-shrink-0" />
            <span className="font-serif">{t('footer.trust.ssl')}</span>
          </div>
          <div className="flex items-center gap-2.5 text-ivory-200/70">
            <ShieldCheck className="w-4 h-4 text-gold-400 flex-shrink-0" />
            <span className="font-serif">{t('footer.trust.gdpr')}</span>
          </div>
        </div>

        {/* Locale marker + legal placeholder */}
        <div
          className="mt-6 pt-6 border-t border-gold-400/20 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[10px] uppercase tracking-[0.32em] font-semibold text-gold-300/60"
          data-testid="footer-locale-strip"
        >
          <span data-testid="footer-locale-marker">{t('footer.localeMarker')}</span>
          <span data-testid="footer-legal-entity">{t('footer.legalEntity')}</span>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gold-400/20 mt-10 pt-6 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-ivory-200/50 font-serif italic">
            © {currentYear} TravelAI. {t('footer.copyright')}
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <button type="button" onClick={() => onNavigate && onNavigate('privacy')} data-testid="footer-bottom-privacy" className="text-sm text-ivory-200/50 hover:text-gold-300 transition-colors font-serif">{t('footer.privacyShort')}</button>
            <button type="button" onClick={() => onNavigate && onNavigate('terms')} data-testid="footer-bottom-terms" className="text-sm text-ivory-200/50 hover:text-gold-300 transition-colors font-serif">{t('footer.termsShort')}</button>
            <button type="button" onClick={() => onNavigate && onNavigate('privacy')} data-testid="footer-bottom-cookies" className="text-sm text-ivory-200/50 hover:text-gold-300 transition-colors font-serif">{t('footer.cookies')}</button>
          </div>
        </div>
      </div>
    </footer>
  );
}
